/**
 * Tentaläge on Home (slice D, P2-1; persona report §4, ruling 9). It turns on by itself 14 days
 * before the course's next exam (`examOn`, then each omtenta in `retakesOn`) and stays on through
 * the exam day, or whenever the student answered "Jag repeterar inför tentan" in Kom igång
 * (`shared/examPeriod.ts`). Without `examOn` only that answer turns it on, and no countdown shows.
 *
 * The card says how many days are left and lays out the steps: write an old exam on time, drill
 * the weakest topics, a timed drill, and the formula sheet. "Bara det vi gått igenom" is off in
 * Tentaläge (ruling 5); release windows still decide what is open (teacher control wins).
 *
 * It renders nothing while the catalogue loads, outside Tentaläge, and while an exam runs. The
 * progress overview is only fetched once the card shows (Home does not read it otherwise).
 */
import { Link } from "react-router";
import { useTopicsAndChapters } from "@client/api/topics";
import { useExamMode } from "@client/app/examMode";
import { Card } from "@client/components/ui";
import { useExamPeriod } from "@client/features/content/course";
import { sitOldExamHref } from "@client/features/content/links";
import { practiceDeepLink } from "@client/features/practice/deepLink";
import { TimedDrillButtons } from "@client/features/practice/sets/TimedDrillButtons";
import { useProgressOverview } from "@client/features/progress/api";
import { weakestTopics } from "@client/features/progress/weakest";
import { useLang, useT, useTp } from "@client/i18n";
import { formatDayShort, formatPercent } from "@client/lib/format";
import type { ExamModeState } from "@shared/examPeriod";

/** The old exam the card offers first: the most recent one in the archive. */
export const EXAM_MODE_PAST_EXAM = "HT25";
/** How many weak topics the card lists; Framsteg shows more. */
const WEAK_SHOWN = 3;

const PRIMARY =
  "inline-flex h-9 items-center justify-center rounded-lg bg-accent-700 px-4 text-sm font-medium text-white transition-colors hover:bg-accent-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:bg-accent-600 dark:hover:bg-accent-500";
const LINK =
  "font-medium text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400";

export function ExamModeCard() {
  const { examActive } = useExamMode();
  const period = useExamPeriod();
  if (examActive || !period?.active) return null;
  return <ExamModeBody period={period} />;
}

function useCountdown(period: ExamModeState): string {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const { exam } = period;
  if (!exam) return t("home.examMode.reviewingNoDate");
  const date = formatDayShort(exam.date, lang);
  if (exam.daysLeft === 0) return t(`home.examMode.today.${exam.kind}`, { date });
  return tp(`home.examMode.daysLeft.${exam.kind}`, exam.daysLeft, { date });
}

function ExamModeBody({ period }: { period: ExamModeState }) {
  const t = useT();
  const { lang } = useLang();
  const countdown = useCountdown(period);
  const overview = useProgressOverview();
  const topics = useTopicsAndChapters();
  const weak = overview.data ? weakestTopics(overview.data.chapters, WEAK_SHOWN) : [];
  const nameOf = (id: string) => {
    const name = topics.data?.topics.find((topic) => topic.id === id)?.name;
    return name ? (name[lang] ?? name.sv) : id;
  };

  return (
    <section aria-labelledby="exam-mode-title" data-testid="exam-mode" className="mb-6">
      <Card compact>
        <p className="text-xs font-medium uppercase tracking-wide text-accent-700 dark:text-accent-400">
          {t("home.examMode.badge")}
        </p>
        <h2
          id="exam-mode-title"
          className="mt-1 font-display text-lg text-ink-900 dark:text-ink-50"
        >
          {countdown}
        </h2>
        {period.reason === "reviewing" && (
          <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
            {t("home.examMode.reviewing")}{" "}
            <Link to="/settings" className={LINK}>
              {t("home.examMode.settings")}
            </Link>
          </p>
        )}
        <ol className="mt-4 space-y-4 text-sm text-ink-800 dark:text-ink-100">
          <li>
            <p className="font-medium">{t("home.examMode.step.exam")}</p>
            <p className="text-ink-600 dark:text-ink-400">{t("home.examMode.step.examHint")}</p>
            <Link to={sitOldExamHref(EXAM_MODE_PAST_EXAM)} className={`${PRIMARY} mt-2`}>
              {t("home.examMode.step.examCta", { exam: EXAM_MODE_PAST_EXAM })}
            </Link>
          </li>
          <li>
            <p className="font-medium">{t("home.examMode.step.weak")}</p>
            {weak.length === 0 ? (
              <p className="text-ink-600 dark:text-ink-400">
                {overview.isLoading ? t("common.loading") : t("home.examMode.step.weakEmpty")}
              </p>
            ) : (
              <ul className="mt-1 space-y-1">
                {weak.map((topic) => (
                  <li key={topic.topicId} className="flex flex-wrap items-baseline gap-x-2">
                    <span>{nameOf(topic.topicId)}</span>
                    <span className="text-xs text-ink-500 dark:text-ink-400">
                      {formatPercent(topic.mastery, lang)}
                    </span>
                    <Link
                      to={practiceDeepLink({ chapters: [topic.chapter], topics: [topic.topicId] })}
                      aria-label={t("progress.weakest.ctaFor", { topic: nameOf(topic.topicId) })}
                      className={LINK}
                    >
                      {t("progress.weakest.cta")}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/progress#svagaste" className={`${LINK} mt-1 inline-block`}>
              {t("home.examMode.step.weakAll")}
            </Link>
          </li>
          <li>
            <p className="font-medium">{t("home.examMode.step.drill")}</p>
            <p className="mb-2 text-ink-600 dark:text-ink-400">{t("practice.drill.hint")}</p>
            <TimedDrillButtons />
          </li>
          <li>
            <p className="font-medium">{t("home.examMode.step.formulas")}</p>
            <p className="text-ink-600 dark:text-ink-400">
              {t("home.examMode.step.formulasHint")}{" "}
              <Link to="/library/formulas" className={LINK}>
                {t("home.examMode.step.formulasCta")}
              </Link>
            </p>
          </li>
        </ol>
        <p className="mt-4 text-xs text-ink-500 dark:text-ink-400">{t("home.examMode.note")}</p>
      </Card>
    </section>
  );
}
